import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { push } from "connected-react-router";
import { getCurrentPath, getRouteParams } from "@selectors";
import { Page } from "react-page-layout";
import { formatDateTime } from "@transformers/date";
import { PersonalDetails } from "@modules/employee/components";
import { RoleSummary } from "@modules/employee/components";
import { Loader, View, ChangeLog, Text } from "@components";
import employeeActionCreators from "@modules/employee/actionCreator";
import { formatAuthoredBy } from "@transformers/names";
import {
  getChangelog,
  getEmployee,
  getEmployeeSchema,
  getEmployeeFormData,
  getEmployeeDraft,
  getEmployeePositionSchema,
  getRoleSummary,
} from "@modules/employee/selectors";
import { isLoading } from "@modules/employee/selectors";
import type { ChangelogType, PropertiesSchema } from "@types";
import type { EmployeeType, EmployeeFormData } from "@modules/employee/types";
import type { RoleSummary as RoleSummaryType } from "@modules/employee/reducer/employment-details/types";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import { getPermissions, getUserType, selectUserType } from "@modules/account/selectors";
import { USER_TYPES } from "@modules/account/constants";

type PropsFromState = $Exact<{
  employeeId?: number,
  isLoading?: boolean,
  employee: EmployeeType,
  employeeFormData: EmployeeFormData,
  changelog?: ChangelogType[],
  currentPath: string,
  schema?: PropertiesSchema,
  employeeDraft: ?Object,
  positionsSchema: PropertiesSchema,
  roleSummary: RoleSummaryType[],
  permissions: { [permission: string]: boolean } | null,
}>;

type PropsFromDispatch = $Exact<{
  redirect: typeof push,
  getEmployeeSchema: typeof employeeActionCreators.GetEmployeeSchema,
  getChangelog: typeof employeeActionCreators.GetEmployeeChangelog,
  resetEmployeeChangelog: typeof employeeActionCreators.ResetEmployeeChangelog,
  onSave: typeof employeeActionCreators.UpdateEmployeeDetails,
  getEmployees: typeof employeeActionCreators.GetEmployees,
  getEmployeePositionSchema: typeof employeeActionCreators.getEmployeePositionSchema,
  getEmployeeRoleSummary: typeof employeeActionCreators.GetEmployeeRoleSummary,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromClientContext,
}>;

export class EmployeeDetails extends React.Component<Props> {
  componentDidMount() {
    if (this.hasAdminPermissions) {
      this.getChangelog(1);
    }

    this.props.getEmployees({
      clientId: this.props.clientId,
      filters: {
        id: this.props.employeeId,
      },
    });

    this.props.getEmployeeSchema({
      clientId: this.props.clientId,
    });

    this.props.getEmployeeRoleSummary({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });

    this.props.getEmployeePositionSchema({
      clientId: this.props.clientId,
    });
  }

  componentWillUnmount() {
    this.props.resetEmployeeChangelog();
  }

  getChangelog = (page: number) =>
    this.props.getChangelog({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      page,
    });

  handlePageChange = (page: number) => {
    this.props.getEmployeeRoleSummary({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      page,
    });
  };

  get hasAdminPermissions() {
    return (
      [USER_TYPES.ADMIN, USER_TYPES.CLIENT_ADMIN, USER_TYPES.SERVICE_MANAGER].indexOf(
        this.props.userType,
      ) !== -1
    );
  }

  get schema() {
    return {
      ...this.props.schema,
      required: [...this.props.schema.required, "employeeRefPrimary"],
    };
  }

  render() {
    return (
      <View gap={8}>
        {this.props.isLoading && <Loader />}
        {this.props.employee && this.props.schema && (
          <View>
            <Text className="message-box" text={this.props.t("EMPLOYEE_DETAILS_TAB_INFO")} />
            <PersonalDetails
              employee={this.props.employee}
              employeeFormData={this.props.employeeFormData}
              employeeDraftData={this.props.employeeDraft}
              clientId={this.props.clientId}
              onSave={this.props.onSave}
              onDeleteEmployee={this.props.onDeleteEmployee}
              currentUserType={this.props.currentUserType}
              currentUserPermissions={this.props.permissions}
              schema={this.schema}
              hasAdminPermissions={this.hasAdminPermissions}
            />
          </View>
        )}
        <RoleSummary
          roleSummary={this.props.roleSummary}
          schema={this.props.positionsSchema}
          employeeId={this.props.employeeId}
          clientId={this.props.clientId}
          onSave={this.props.onSave}
          hasAdminPermissions={this.hasAdminPermissions}
          handlePageChange={this.handlePageChange}
        />
        {this.props.changelog && (
          <ChangeLog entries={this.props.changelog} t={this.props.t} formatDate={formatDateTime} />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);

  return {
    isLoading: isLoading(state),
    currentPath: getCurrentPath(state),
    employee: getEmployee(employeeId)(state),
    employeeFormData: getEmployeeFormData(employeeId)(state),
    changelog: getChangelog(state),
    schema: getEmployeeSchema(state),
    employeeId,
    employeeDraft: getEmployeeDraft(state),
    positionsSchema: getEmployeePositionSchema(state),
    roleSummary: getRoleSummary(state),
    permissions: getPermissions(state),
    currentUserType: getUserType(state),
    userType: selectUserType(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  redirect: push,
  getEmployeeSchema: employeeActionCreators.GetEmployeeSchema,
  getChangelog: employeeActionCreators.GetEmployeeChangelog,
  resetEmployeeChangelog: employeeActionCreators.ResetEmployeeChangelog,
  onSave: employeeActionCreators.UpdateEmployeeDetails,
  onDeleteEmployee: employeeActionCreators.DeleteEmployee,
  getEmployees: employeeActionCreators.GetEmployees,
  getEmployeePositionSchema: employeeActionCreators.GetEmployeePositionSchema,
  getEmployeeRoleSummary: employeeActionCreators.GetEmployeeRoleSummary,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withClientContext(),
  withPermissions({ permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES }),
)(EmployeeDetails);
